import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { GoldenAmount, OrderStatusBadge } from '../components/store/StoreUi';

const FILTERS = ['all', 'placed', 'processing', 'packed', 'shipped', 'delivered', 'cancelled'];

export default function StoreOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/store/orders', {
        params: { page, limit: 10, status: status === 'all' ? undefined : status },
      });
      setOrders(data?.items || []);
      setPages(data?.pages || 1);
    } catch (err) {
      setError(err?.response?.data?.message || 'Orders could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [status]);

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-8 sm:py-10">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Link to="/store" className="text-sm font-semibold text-amber-700 dark:text-amber-400 hover:underline">← Back to store</Link>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black text-slate-950 dark:text-white">My orders</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Track purchases, shipping, delivery, and refunds.</p>
          </div>
          <Link to="/store/cart" className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200">
            View cart
          </Link>
        </div>

        <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              onClick={() => setStatus(filter)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold capitalize ${
                status === filter
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-slate-950'
                  : 'border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-700">{error}</div>
        ) : orders.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-white/15 bg-white dark:bg-slate-900 p-14 text-center">
            <p className="text-5xl" aria-hidden>📦</p>
            <h2 className="mt-4 text-xl font-black text-slate-900 dark:text-white">No {status === 'all' ? '' : status} orders yet</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Your store purchases will appear here.</p>
            <Link to="/store" className="mt-5 inline-flex rounded-xl bg-amber-400 px-5 py-3 font-black text-slate-950">Shop rewards</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <article key={order._id} className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-white/5 px-4 py-3 sm:px-5">
                  <div>
                    <p className="font-black text-slate-900 dark:text-white">{order.orderNumber}</p>
                    <p className="text-xs text-slate-500">{new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <OrderStatusBadge status={order.status} />
                    <GoldenAmount amount={order.totalGolden} />
                  </div>
                </div>
                <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                  <div className="flex -space-x-2">
                    {order.items.slice(0, 4).map((item, index) => (
                      <div key={`${item.product}-${index}`} className="h-14 w-14 overflow-hidden rounded-xl border-2 border-white dark:border-slate-900 bg-slate-100 dark:bg-slate-800">
                        {item.image ? <img src={item.image} alt={item.productName} className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center">🎁</span>}
                      </div>
                    ))}
                    {order.items.length > 4 && (
                      <span className="flex h-14 w-14 items-center justify-center rounded-xl border-2 border-white dark:border-slate-900 bg-slate-800 text-xs font-black text-white">
                        +{order.items.length - 4}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 sm:px-3">
                    <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {order.items.map((item) => `${item.quantity}× ${item.productName}`).join(', ')}
                    </p>
                    {order.tracking?.trackingNumber && (
                      <p className="mt-1 text-xs text-slate-500">Tracking: {order.tracking.trackingNumber}</p>
                    )}
                  </div>
                  <Link to={`/store/orders/${order._id}`} className="rounded-xl bg-slate-950 dark:bg-white px-4 py-2.5 text-center text-sm font-bold text-white dark:text-slate-950">
                    Track order
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-xl border px-4 py-2 text-sm font-bold disabled:opacity-40">Previous</button>
            <span className="text-sm text-slate-500">Page {page} of {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-xl border px-4 py-2 text-sm font-bold disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </main>
  );
}
